import React, { useEffect, useRef, useState } from "react";
import IconLoader from "@/features/Common/components/IconLoader";
import { Box, Button } from "@mui/material";
import { WellnessCategoryScoreSection } from "../Common/WellnessCategoryScoreSection";
import WellnessCategoryCard from "../WellnessCategoryCard";
import { useTranslation } from "react-i18next";
import HealthScoreIndicator from "../Common/HealthScoreIndicator";
import CollapsableMetricsList from "../Common/CollapsableMetricsList";
import {
  WellnessCategoryCardProps,
  WellnessScoreStatus,
  getWellnessScoreStatusFromScore,
} from "../Common/types";
import { useMachine } from "@xstate/react";
import {
  mentalWellbeingMachine,
  mentalWellbeingEventNames,
  MentalWellbeingState,
} from "./machine";
import MentalWellbeingScreen from "./MentalWellbeingScreen";
import MentalWellbeingQuestionnaireContent from "../../pages/MentalWellbeingQuestionnaire/MentalWellbeingQuestionnaireContent";
import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import { getMentalWellbeingMetricsFromHealthProfile } from "./utils/getMentalWellbeingMetricsFromHealthProfile";
import useHealthTrends from "../../hooks/useHealthTrends";
import { mapHealthTrendsToMentalWellbeingChartData } from "../../utils/healthTrendsChartData";
import { capMentalWellbeingScore } from "../../utils/capMentalWellbeingScore";
import { wellnessCategoryFullScreenOverlaySx } from "../Common/wellnessCategoryFullScreenOverlaySx";

export interface MentalWellbeingCategoryProps {
  healthProfile?: HealthProfileSuccessResponseData | null;
  refetch?: () => void;
  domainScore?: number;
  domainCompleteness?: number;
  domainLastUpdated?: string;
}

export default function MentalWellbeingCategory({
  healthProfile,
  refetch: _refetch,
  domainScore,
  domainCompleteness,
}: MentalWellbeingCategoryProps = {}) {
  const { t } = useTranslation();
  const [state, send] = useMachine(mentalWellbeingMachine);
  const mentalWellbeingSectionRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef(state.value);
  const [listExpanded, setListExpanded] = useState(false);

  const { data: depressionTrends } = useHealthTrends("Phq2Score");
  const { data: anxietyTrends } = useHealthTrends("Gad2Score");
  const depressionChartData = mapHealthTrendsToMentalWellbeingChartData(
    depressionTrends ?? []
  );
  const anxietyChartData = mapHealthTrendsToMentalWellbeingChartData(
    anxietyTrends ?? []
  );

  const lastUpdated = [
    ...(depressionTrends ?? []),
    ...(anxietyTrends ?? []),
  ].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )[0]?.timestamp;

  let { anxiety, depression } =
    getMentalWellbeingMetricsFromHealthProfile(
      healthProfile,
      t as (key: string) => string
    ) ?? {};

  const hasData =
    !!anxiety &&
    !!depression &&
    domainScore != null &&
    domainCompleteness != null &&
    domainCompleteness > 0;

  useEffect(() => {
    const wasDetailOrQuestionnaire =
      prevStateRef.current === MentalWellbeingState.MentalWellbeingScreen ||
      prevStateRef.current === MentalWellbeingState.Questionnaire;
    if (
      wasDetailOrQuestionnaire &&
      state.matches(MentalWellbeingState.Dashboard)
    ) {
      setListExpanded(true);
      requestAnimationFrame(() => {
        const el = mentalWellbeingSectionRef.current;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    prevStateRef.current = state.value;
  }, [state.value]);

  const handleBack = () => {
    send({ type: mentalWellbeingEventNames.BACK });
  };

  const handleMetricClick = () => {
    send({ type: mentalWellbeingEventNames.OPEN_SCREEN });
  };

  const handleRetakeAssessment = () => {
    send({ type: mentalWellbeingEventNames.OPEN_QUESTIONNAIRE });
  };

  const handleQuestionnaireClose = () => {
    send({ type: mentalWellbeingEventNames.BACK_TO_SCREEN });
  };

  const anxietyScore = anxiety?.score ?? 0;
  const depressionScore = depression?.score ?? 0;

  const cappedDomainScore =
    domainScore != null
      ? capMentalWellbeingScore(domainScore, anxietyScore, depressionScore)
      : undefined;

  const categoryStatus =
    cappedDomainScore != null
      ? getWellnessScoreStatusFromScore(cappedDomainScore, domainCompleteness)
      : WellnessScoreStatus.Healthy;

  /** Indicator score (0-100) so the pin and colour match the category zone: red < 20, yellow 20-60, green > 60 */
  // 12 points should give an indicator of 0 and 0 points an indicator of 100
  const rawIndicatorScore = 100 - ((anxietyScore + depressionScore) * 100) / 12;
  const indicatorScore = capMentalWellbeingScore(
    rawIndicatorScore,
    anxietyScore,
    depressionScore
  );

  const mentalWellbeingProps: WellnessCategoryCardProps = {
    icon: (
      <IconLoader
        icon="MentalWellbeingIcon"
        color="primary"
        sx={{ fontSize: 40 }}
      />
    ),
    title: "Mental Wellbeing",
    status: hasData ? categoryStatus : undefined,
    description: hasData
      ? t("MentalWellbeing.description.update")
      : t("MentalWellbeing.description"),
    lastUpdated: lastUpdated,
  };

  if (state.matches(MentalWellbeingState.Questionnaire)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <MentalWellbeingQuestionnaireContent
          onClose={handleQuestionnaireClose}
          onSubmitComplete={handleQuestionnaireClose}
        />
      </Box>
    );
  }

  if (state.matches(MentalWellbeingState.MentalWellbeingScreen)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <MentalWellbeingScreen
          depressionChartData={depressionChartData}
          anxietyChartData={anxietyChartData}
          anxietyScore={anxiety?.score ?? 0}
          depressionScore={depression?.score ?? 0}
          lastUpdated={lastUpdated}
          onBack={handleBack}
          onRetakeAssessment={handleRetakeAssessment}
        />
      </Box>
    );
  }

  return (
    <Box ref={mentalWellbeingSectionRef}>
      <WellnessCategoryCard {...mentalWellbeingProps}>
        {hasData && anxiety && depression && (
          <WellnessCategoryScoreSection>
            <HealthScoreIndicator healthScore={indicatorScore} />

            <CollapsableMetricsList
              metrics={[anxiety, depression]}
              onMetricClick={handleMetricClick}
              expanded={listExpanded}
              onExpandedChange={setListExpanded}
            />
          </WellnessCategoryScoreSection>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleRetakeAssessment}
          fullWidth
        >
          {hasData
            ? t("MentalWellbeing.buttonLabel.primary.update")
            : t("MentalWellbeing.buttonLabel.primary")}
        </Button>
      </WellnessCategoryCard>
    </Box>
  );
}
